import Seo from "@/components/Seo";
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Mail, ArrowUpRight, Loader2, Check, ImagePlus, X } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CONTACT } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["Single fixture / repair", "Renovation", "Commercial / new build", "Municipal", "Other"];

const empty = { name: "", email: "", phone: "", company: "", project_type: "", message: "", website: "" };

const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const PHOTO_MAX = 10 * 1024 * 1024;

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const photoRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error("Photos must be JPG, PNG or WEBP.");
      e.target.value = "";
      return;
    }
    if (file.size > PHOTO_MAX) {
      toast.error("Photo too large — max 10MB.");
      e.target.value = "";
      return;
    }
    setPhoto(file);
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (photoRef.current) photoRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      let photo_path = "";
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const { data } = await axios.post(`${API}/quotes/photo`, fd);
        photo_path = data.photo_path;
      }
      await axios.post(`${API}/quotes`, { ...form, photo_path });
      setForm(empty);
      clearPhoto();
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border-0 border-b border-white/20 rounded-none px-4 py-4 text-white placeholder-white/30 focus:outline-none focus:border-gold transition-colors duration-300";

  return (
    <div>
      <Seo
        title="Contact Saver Plus Lighting Corp (SPLC) | Toronto Lighting & Electrical Supplier"
        description="Contact Saver Plus Lighting Corp (SPLC) — 17 Henrietta St, Toronto, ON M6N 1S4. Call 416-767-0960 for wholesale or retail lighting and electrical supply. Open 7am–7pm, no minimum order."
        path="/contact"
      />
      <section className="pt-40 md:pt-56 pb-12 max-w-[1400px] mx-auto px-5 md:px-10" data-testid="contact-hero">
        <p className="text-xs uppercase tracking-[0.28em] text-gold/90 font-semibold mb-6 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-gold" /> Contact
        </p>
        <h1 className="font-display uppercase font-medium text-[13vw] md:text-[8vw] leading-[0.85] tracking-tighter">
          <MaskedLines lines={["Request", "a quote."]} once={false} />
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-white/70 leading-relaxed">
                Tell us what you need — one fixture or one thousand. Quick quotations, dependable supply, and a real
                specialist on the other end.
              </p>
              <ul className="mt-10 space-y-6">
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-gold mt-1 shrink-0" />
                  <a href={CONTACT.phoneHref} className="link-sweep text-white/85" data-testid="contact-phone">{CONTACT.phone}</a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-gold mt-1 shrink-0" />
                  <a href={CONTACT.emailHref} className="link-sweep text-white/85 break-all">{CONTACT.email}</a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-gold mt-1 shrink-0" />
                  <a
                    href={CONTACT.mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-sweep text-white/85 hover:text-white"
                    data-testid="contact-address"
                  >
                    {CONTACT.street}, {CONTACT.city}, {CONTACT.region} {CONTACT.postalCode}, {CONTACT.country}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-gold mt-1 shrink-0" />
                  <span className="text-white/85">{CONTACT.hours}</span>
                </li>
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.15}>
              <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
                  {/* Honeypot — hidden from users, catches bots */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={form.website}
                    onChange={update("website")}
                    className="absolute left-[-9999px] w-px h-px overflow-hidden opacity-0"
                    data-testid="hp-website"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input data-testid="input-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={update("name")} />
                    <input data-testid="input-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={update("email")} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input data-testid="input-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={update("phone")} />
                    <input data-testid="input-company" className={inputCls} placeholder="Company (optional)" value={form.company} onChange={update("company")} />
                  </div>
                  <select data-testid="input-project-type" className={`${inputCls} appearance-none`} value={form.project_type} onChange={update("project_type")}>
                    <option value="" className="bg-[#121212]">Project type</option>
                    {PROJECT_TYPES.map((p) => (
                      <option key={p} value={p} className="bg-[#121212]">{p}</option>
                    ))}
                  </select>
                  <textarea data-testid="input-message" rows={5} className={inputCls} placeholder="Tell us what you need *" value={form.message} onChange={update("message")} />
                  <div>
                    <label
                      className="flex items-center gap-3 border border-white/20 px-4 py-4 cursor-pointer hover:border-gold transition-colors duration-300"
                      data-testid="photo-attach-label"
                    >
                      <ImagePlus className="w-4 h-4 text-gold shrink-0" />
                      <span className={`text-sm truncate ${photo ? "text-white" : "text-white/40"}`}>
                        {photo ? photo.name : "Attach a photo (optional)"}
                      </span>
                      {photo && (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); clearPhoto(); }}
                          className="ml-auto shrink-0 text-white/40 hover:text-red-400 transition-colors"
                          aria-label="Remove photo"
                          data-testid="photo-remove-button"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <input
                        ref={photoRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={pickPhoto}
                        data-testid="input-photo"
                      />
                    </label>
                    <p className="mt-2 text-xs text-white/35">
                      A photo of the fixture or part helps us source hard-to-find items faster. JPG, PNG or WEBP, max 10MB.
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-submit-button"
                    className="inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 hover:bg-[#b00000] transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send Request <ArrowUpRight className="w-4 h-4" /></>}
                  </button>
                </form>
            </Reveal>
          </div>
        </div>
      </section>

      <Dialog open={done} onOpenChange={setDone}>
        <DialogContent
          data-testid="quote-success-modal"
          className="border border-white/10 bg-[#121212] text-white rounded-none max-w-md p-10 md:p-12 gap-0 [&>button]:text-white/50 [&>button]:hover:text-white"
        >
          <div className="grid place-items-center w-14 h-14 border border-gold/40 mb-6">
            <Check className="w-6 h-6 text-gold" />
          </div>
          <h3 className="font-display uppercase text-3xl tracking-tight leading-none">Request received</h3>
          <p className="mt-4 text-white/60 leading-relaxed">
            Thanks — a lighting specialist will reach out shortly. Need it sooner? Call us at{" "}
            <a href={CONTACT.phoneHref} className="text-gold">{CONTACT.phone}</a>.
          </p>
          <button
            onClick={() => setDone(false)}
            data-testid="quote-success-close"
            className="mt-8 inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-6 py-3.5 hover:bg-[#b00000] transition-colors duration-300"
          >
            Done
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
